import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CalendarDays, User, Globe, CircleDot, Eye, Pencil, Upload, Trash2, Undo } from 'lucide-react';
import { useStrategicPillar, useDeleteStrategicPillar, useUpdateStrategicPillarStatus } from '@/lib/api/hooks/strategic-pillar';
import { ActionDropdown, DeleteModal, PublishModal, UnpublishModal, type DropdownAction } from '@/components/ui';
import ArticleContent from '@/components/ui/ArticleContent';
import { showToast } from '@/lib/toast';

const StrategicPillarDetailPage: React.FC = () => {
  const { id: strategicPillarId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [statusModalOpen, setStatusModalOpen] = useState(false);

  const { data: strategicPillarData, isLoading, error } = useStrategicPillar(strategicPillarId || '');
  const article = strategicPillarData?.data;

  const { mutate: deleteStrategicPillar, isPending: isDeleting } = useDeleteStrategicPillar();
  const { mutate: updateStatus, isPending: isUpdatingStatus } = useUpdateStrategicPillarStatus();

  if (!strategicPillarId) {
    return (
      <div className="text-center p-8 bg-white rounded-lg">
        <h2 className="text-xl font-bold">Strategic Pillar ID missing</h2>
        <button onClick={() => navigate('/admin/strategic-pillars')} className="mt-4 text-[#681628] hover:underline">
          &larr; Back to Strategic Pillars
        </button>
      </div>
    );
  }



  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' });
    } catch {
      return dateString;
    }
  };

  const handleEdit = () => {
    navigate(`/admin/strategic-pillars/${strategicPillarId}/edit`);
  };

  const handleDeleteClick = () => {
    setDeleteModalOpen(true);
  };

  const handleStatusUpdateClick = () => {
    if (!article) return;
    setStatusModalOpen(true);
  };

  const confirmDelete = () => {
    deleteStrategicPillar(strategicPillarId, {
      onSuccess: (response) => {
        if (response.success) {
          showToast.success('Strategic pillar deleted successfully!');
          setDeleteModalOpen(false);
          navigate('/admin/strategic-pillars');
        }
      },
      onError: (error: Error) => {
        showToast.error(error);
      }
    });
  };

  const confirmStatusUpdate = () => {
    if (!article) return;
    const newStatus = article.status === 'PUBLISH' ? 'DRAFT' : 'PUBLISH';

    updateStatus(
      { strategicPillarId, status: newStatus },
      {
        onSuccess: (response) => {
          if (response.success) {
            showToast.success(
              newStatus === 'PUBLISH'
                ? 'Strategic pillar published successfully!'
                : 'Strategic pillar unpublished successfully!'
            );
            setStatusModalOpen(false);
          }
        },
        onError: (error: Error) => {
          showToast.error(error);
        }
      }
    );
  };

  const getDropdownActions = (): DropdownAction[] => [
    {
      label: 'Edit',
      onClick: (e) => {
        e.stopPropagation();
        handleEdit();
      }
    },
    {
      label: article?.status === 'PUBLISH' ? 'Un-Publish' : 'Publish',
      onClick: (e) => {
        e.stopPropagation();
        handleStatusUpdateClick();
      },
      disabled: isUpdatingStatus
    },
    {
      label: 'Delete',
      onClick: (e) => {
        e.stopPropagation();
        handleDeleteClick();
      },
      variant: 'danger' as const,
      disabled: isDeleting
    }
  ];

  const quickActions = [
    {
      key: 'edit',
      label: 'Edit',
      icon: Pencil,
      onClick: handleEdit,
      className: 'text-gray-700 border-gray-200 hover:bg-gray-50',
      disabled: false,
    },
    {
      key: 'status',
      label: article?.status === 'PUBLISH' ? 'Un-Publish' : 'Publish',
      icon: article?.status === 'PUBLISH' ? Undo : Upload,
      onClick: handleStatusUpdateClick,
      className: 'text-gray-700 border-gray-200 hover:bg-gray-50',
      disabled: isUpdatingStatus,
    },
    {
      key: 'delete',
      label: 'Delete',
      icon: Trash2,
      onClick: handleDeleteClick,
      className: 'text-red-600 border-red-200 hover:bg-red-50',
      disabled: isDeleting,
    },
  ];

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-10">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-wine-100"></div>
          <p className="mt-4 text-sm text-gray-500">Loading strategic pillar...</p>
        </div>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="text-center p-8 bg-white rounded-lg">
        <h2 className="text-xl font-bold">Strategic Pillar not found</h2>
        <p className="text-sm text-gray-500 mt-2">{error?.message || 'The strategic pillar could not be found'}</p>
        <button onClick={() => navigate('/admin/strategic-pillars')} className="mt-4 text-[#681628] hover:underline">
          &larr; Back to Strategic Pillars
        </button>
      </div>
    );
  }

  const isUnpublishing = article?.status === 'PUBLISH';
  const authorName = article.createdByUsername || article.createdBy;
  const publishedOn = article.datePublished || article.createdAt;
  const lastUpdated = (article as unknown as { updatedAt?: string }).updatedAt || article.createdAt;

  return (
    <>
      <div className="space-y-6">
        <div className="text-sm text-gray-500">
          <span className="cursor-pointer hover:underline" onClick={() => navigate('/admin/strategic-pillars')}>Strategic Pillars</span>
          <span className="mx-2">&gt;</span>
          <span className="font-medium text-gray-700">{article.title}</span>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_360px] gap-6 items-start">
          <div className="space-y-6">
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
              <div className="flex flex-col md:flex-row md:items-start gap-6">
                {article.coverImage && (
                  <img src={article.coverImage} alt={article.title} className="w-full md:w-60 h-44 object-cover rounded-xl flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-3">
                    <h1 className="text-3xl font-semibold text-gray-900">{article.title}</h1>
                    <ActionDropdown id={strategicPillarId} actions={getDropdownActions()} />
                  </div>

                  <div className="flex flex-wrap items-center gap-3 mt-4 text-sm text-gray-600">
                    <span className="inline-flex items-center gap-1.5"><CalendarDays className="w-4 h-4" />{formatDate(publishedOn)}</span>
                    <span className="text-gray-300">|</span>
                    <span className="inline-flex items-center gap-1.5"><User className="w-4 h-4" />By {authorName}</span>
                    <span className={`inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-medium ${article.status === 'PUBLISH' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
                      <span className={`h-2 w-2 rounded-full ${article.status === 'PUBLISH' ? 'bg-emerald-500' : 'bg-amber-400'}`} />
                      {article.status === 'PUBLISH' ? 'Published' : 'Draft'}
                    </span>
                  </div>

                  <div className="flex flex-wrap items-center gap-3 mt-4 text-sm text-gray-600">
                    <span className="inline-flex items-center gap-1.5"><Globe className="w-4 h-4" />Language: <span className="font-medium text-gray-800">English (EN)</span></span>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4">
              <ArticleContent content={article.content || ''} className="" />
            </div>
          </div>

          <aside className="xl:sticky xl:top-24 space-y-4">
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5">
              <h3 className="text-lg font-semibold text-gray-900">Strategic Pillar Details</h3>

              <div className="mt-4 space-y-3 text-sm">
                <div className="flex justify-between gap-3 items-start">
                  <span className="text-gray-500 inline-flex items-center gap-2"><User className="w-4 h-4" />Author</span>
                  <span className="text-gray-900 font-medium text-right">{authorName}</span>
                </div>
                <div className="flex justify-between gap-3 items-start">
                  <span className="text-gray-500 inline-flex items-center gap-2"><CalendarDays className="w-4 h-4" />Published On</span>
                  <span className="text-gray-900 font-medium text-right">{formatDate(publishedOn)}</span>
                </div>
                <div className="flex justify-between gap-3 items-start">
                  <span className="text-gray-500 inline-flex items-center gap-2"><CalendarDays className="w-4 h-4" />Last Updated</span>
                  <span className="text-gray-900 font-medium text-right">{formatDate(lastUpdated)}</span>
                </div>
                <div className="flex justify-between gap-3 items-start">
                  <span className="text-gray-500 inline-flex items-center gap-2"><Globe className="w-4 h-4" />Language</span>
                  <span className="text-gray-900 font-medium text-right">English (EN)</span>
                </div>
                <div className="flex justify-between gap-3 items-start">
                  <span className="text-gray-500 inline-flex items-center gap-2"><CircleDot className="w-4 h-4" />Status</span>
                  <span className={`inline-flex items-center gap-1 text-xs font-medium ${article.status === 'PUBLISH' ? 'text-emerald-700' : 'text-amber-700'}`}>
                    <span className={`h-2 w-2 rounded-full ${article.status === 'PUBLISH' ? 'bg-emerald-500' : 'bg-amber-400'}`} />
                    {article.status === 'PUBLISH' ? 'Published' : 'Draft'}
                  </span>
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5">
              <h3 className="text-lg font-semibold text-gray-900">Engagement Overview</h3>
              <div className="grid grid-cols-1 gap-3 mt-4">
                <div className="rounded-lg border border-gray-200 p-3">
                  <p className="text-xs text-gray-500 inline-flex items-center gap-1.5"><Eye className="w-3.5 h-3.5" />Views</p>
                  <p className="text-xl font-semibold text-gray-900 mt-1">{(article as unknown as { totalViews?: number }).totalViews || 0}</p>
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5">
              <h3 className="text-lg font-semibold text-gray-900">Quick Actions</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
                {quickActions.map((action) => {
                  const ActionIcon = action.icon;

                  return (
                    <button
                      key={action.key}
                      type="button"
                      onClick={action.onClick}
                      disabled={action.disabled}
                      className={`inline-flex items-center justify-center gap-2.5 px-4 py-3 rounded-lg border text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${action.className}`}
                    >
                      <ActionIcon className="w-4 h-4" />
                      {action.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </aside>
        </div>
      </div>

      <DeleteModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={confirmDelete}
        title="Delete Strategic Pillar?"
        subtitle="Are you sure you want to delete this strategic pillar?"
        itemName={article?.title}
        warningText="This action cannot be undone. The strategic pillar will be permanently deleted."
        itemType="Strategic Pillar"
        isLoading={isDeleting}
      />


      {isUnpublishing ? (
        <UnpublishModal
          isOpen={statusModalOpen}
          onClose={() => setStatusModalOpen(false)}
          onConfirm={confirmStatusUpdate}
          itemType="Strategic Pillar"
          isLoading={isUpdatingStatus}
        />
      ) : (
        <PublishModal
          isOpen={statusModalOpen}
          onClose={() => setStatusModalOpen(false)}
          onConfirm={confirmStatusUpdate}
          title="Publish Strategic Pillar?"
          subtitle="Are you sure you want to publish this strategic pillar?"
          itemName={article?.title}
          infoText="It will be displayed on your website."
          itemType="Strategic Pillar"
          isLoading={isUpdatingStatus}
        />
      )}
    </>
  );
};

export default StrategicPillarDetailPage;
